// auth.interceptor.ts
import { environment } from "@environments/environment";
import { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpResponse } from "@angular/common/http";
import { inject } from "@angular/core";
import { Router } from "@angular/router";
import { JwtHelperService } from "@auth0/angular-jwt";
import { AuthService } from "@auth0/auth0-angular";
import { concatMap, map, of, switchMap, throwError, filter, take } from "rxjs";
import { catchError } from "rxjs/operators";
import { API_CONST } from "../constants/api.constants";

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const jwtHelper = new JwtHelperService();
  
  if (!req.url.includes(environment.apiUrl)) {
    return next(req);
  }

  if (
    req.url.includes(API_CONST.USER.BUSINESS_SIGNUP) ||
    req.url.includes(API_CONST.USER.RESEND_VERIFICATION) ||
    req.url.includes(API_CONST.USER.VERIFY_EMAIL) ||
    req.url.includes(API_CONST.PROJECT.GET_CURRENCIES)
  ) {
    return next(req);
  }

  // Wait for Auth0 to finish loading before attempting to get token
  return auth.isLoading$.pipe(
    filter(loading => !loading),
    take(1),
    switchMap(() => 
      auth.getAccessTokenSilently({
        detailedResponse: true,
        cacheMode: "on",
      }).pipe(
        catchError((error) => {
          // Handle missing refresh token or authentication errors
          if (
            error.error === 'login_required' || 
            error.error === 'consent_required' ||
            error.message?.includes('Missing Refresh Token') ||
            error.error_description?.includes('Missing Refresh Token')
          ) {
            console.warn("Authentication required, redirecting to login");
            // Redirect to login
            auth.loginWithRedirect({
              appState: { target: router.url }
            });
            return throwError(() => new Error('Authentication required'));
          }
          
          // If silent token retrieval fails, try with cache off
          return auth.getAccessTokenSilently({
            detailedResponse: true,
            cacheMode: "off",
          }).pipe(
            catchError((retryError) => {
              // If retry also fails, redirect to login
              if (
                retryError.error === 'login_required' || 
                retryError.error === 'consent_required' ||
                retryError.message?.includes('Missing Refresh Token') ||
                retryError.error_description?.includes('Missing Refresh Token')
              ) {
                console.warn("Authentication required after retry, redirecting to login");
                auth.loginWithRedirect({
                  appState: { target: router.url }
                });
                return throwError(() => new Error('Authentication required'));
              }
              return throwError(() => retryError);
            })
          );
        })
      )
    ),
    switchMap((tokenResponse) => {
      if (jwtHelper.isTokenExpired(tokenResponse.access_token)) {
        return auth.getAccessTokenSilently({
          detailedResponse: true,
          cacheMode: "off",
        }).pipe(
          catchError((error) => {
            // Handle missing refresh token when trying to refresh expired token
            if (
              error.error === 'login_required' || 
              error.error === 'consent_required' ||
              error.message?.includes('Missing Refresh Token') ||
              error.error_description?.includes('Missing Refresh Token')
            ) {
              console.warn("Token expired and refresh failed, redirecting to login");
              auth.loginWithRedirect({
                appState: { target: router.url }
              });
              return throwError(() => new Error('Authentication required'));
            }
            return throwError(() => error);
          })
        );
      }
      return of(tokenResponse);
    }),
    concatMap((tokenResponse) => {
      req = req.clone({
        setHeaders: {
          Authorization: `Bearer ${tokenResponse.access_token}`,
        },
      });
      return next(req).pipe(
        map((event: HttpEvent<any>) => {
          if (event instanceof HttpResponse) {
            return event.clone({
              body: event.body?.data,
            });
          }
          return event;
        }),
        catchError((error: any) => {
          if (error instanceof HttpErrorResponse) {
            // Handle 401 Unauthorized - token might be invalid or expired
            if (error.status === 401) {
              console.warn("Received 401 Unauthorized, redirecting to login");
              auth.loginWithRedirect({
                appState: { target: router.url }
              });
              return throwError(() => new Error('Unauthorized - authentication required'));
            }
            console.error("HTTP error:", error);
          } else {
            console.error("Non-HTTP error:", error);
          }
          return throwError(() => error);
        })
      );
    })
  );
};
